import React, { useEffect, useId, useRef, useState } from 'react';
import { ChipGroup, ChipOption } from './ChipGroup';
import { submitContactForm } from '../../services/api';

interface BriefModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const STAGE_OPTIONS: ChipOption[] = [
  { value: 'idea',         label: 'Just an idea' },
  { value: 'requirements', label: 'Have requirements' },
  { value: 'prototype',    label: 'Have a prototype' },
  { value: 'product',      label: 'Have a product' },
];

const TIMELINE_OPTIONS: ChipOption[] = [
  { value: 'this-quarter', label: 'This quarter' },
  { value: 'next-quarter', label: 'Next quarter' },
  { value: 'exploring',    label: 'Exploring — no rush' },
];

const stageLabelFor = (v: string | null) =>
  STAGE_OPTIONS.find(o => o.value === v)?.label ?? 'unspecified stage';
const timelineLabelFor = (v: string | null) =>
  TIMELINE_OPTIONS.find(o => o.value === v)?.label ?? 'unspecified timeline';

/**
 * Accessible "Send a brief" dialog.
 *
 * User-locked accessibility (CONTEXT.md):
 * - ESC closes
 * - Click on backdrop closes
 * - Focus trap inside dialog while open (Tab + Shift+Tab cycle)
 * - Focus moves into the dialog on open (close button is first focus)
 * - Focus returns to the trigger element after close (parent responsibility:
 *   capture activeElement before setIsOpen(true), then refocus on close)
 * - role=dialog, aria-modal, aria-labelledby on the heading
 * - Success state announced via aria-live="polite"
 *
 * Submission (B2 fix): POSTs to /api/contact/submit via submitContactForm —
 * same helper the legacy frontend/src/pages/Contact.tsx uses. Endpoint is a
 * stub today (returns success without delivering email); wiring it to the
 * existing Gmail SMTP integration is a follow-up phase.
 *
 * Scroll-lock (W7 fix): iOS-safe position:fixed pattern. Capture
 * window.scrollY, fix the body, restore scroll position on close. The naive
 * overflow:hidden pattern does NOT prevent scrolling on iOS Safari and is
 * rejected.
 */
export const BriefModal: React.FC<BriefModalProps> = ({ isOpen, onClose }) => {
  const headingId = useId();
  const backdropRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stage, setStage] = useState<string | null>(null);
  const [timeline, setTimeline] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');

  // Reset state shortly after close so reopening shows a clean form.
  useEffect(() => {
    if (!isOpen) {
      const t = setTimeout(() => {
        setSubmitted(false);
        setSubmitting(false);
        setError(null);
        setStage(null);
        setTimeline(null);
        setName('');
        setEmail('');
        setMessage('');
      }, 250);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  // On open: focus close button + iOS-safe scroll lock.
  useEffect(() => {
    if (!isOpen) return;
    const raf = requestAnimationFrame(() => {
      closeButtonRef.current?.focus();
    });

    const scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';

    return () => {
      cancelAnimationFrame(raf);
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, scrollY);
    };
  }, [isOpen]);

  // Focus trap + ESC.
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const root = innerRef.current;
      if (!root) return;
      const focusables = Array.from(
        root.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter(el => el.offsetParent !== null || el === document.activeElement);
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  const onBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === backdropRef.current) onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);

    const stageLabel = stageLabelFor(stage);
    const timelineLabel = timelineLabelFor(timeline);
    const subject = `[Brief] ${stageLabel} — ${timelineLabel}`;
    const fullMessage = `${message.trim()}\n\n---\nStage: ${stageLabel}\nTimeline: ${timelineLabel}`;

    try {
      const result = await submitContactForm({
        name: name.trim(),
        email: email.trim(),
        subject,
        message: fullMessage,
      });
      if (result.error) {
        setError(
          'Something went wrong sending the brief — please try again, or email us directly at hello@intrnauts.ai.'
        );
        setSubmitting(false);
        return;
      }
      setSubmitted(true);
    } catch (err) {
      setError(
        'Something went wrong sending the brief — please try again, or email us directly at hello@intrnauts.ai.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      ref={backdropRef}
      className="ai-modal-backdrop"
      onClick={onBackdropClick}
      role="presentation"
    >
      <div
        ref={innerRef}
        className="ai-modal-inner"
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
      >
        <button
          type="button"
          ref={closeButtonRef}
          className="ai-modal-close"
          onClick={onClose}
          aria-label="Close brief form"
        >
          ×
        </button>

        {!submitted ? (
          <>
            <h3 id={headingId} className="ai-modal-h3">Send a brief.</h3>
            <p className="ai-modal-lede">Three quick fields plus two taps. We'll reply within a business day.</p>
            <form className="ai-modal-form" onSubmit={handleSubmit}>
              <label htmlFor={`${headingId}-name`}>Your name</label>
              <input
                id={`${headingId}-name`}
                name="name"
                type="text"
                placeholder="Jordan Park"
                required
                value={name}
                onChange={e => setName(e.target.value)}
              />

              <label htmlFor={`${headingId}-email`}>Email</label>
              <input
                id={`${headingId}-email`}
                name="email"
                type="email"
                placeholder="jordan@company.com"
                required
                value={email}
                onChange={e => setEmail(e.target.value)}
              />

              <label htmlFor={`${headingId}-message`}>Tell us about what you're trying to solve</label>
              <textarea
                id={`${headingId}-message`}
                name="message"
                placeholder="We're trying to…"
                required
                value={message}
                onChange={e => setMessage(e.target.value)}
              />

              <label>Where are you at?</label>
              <ChipGroup
                id={`${headingId}-stage`}
                label="Where are you at?"
                options={STAGE_OPTIONS}
                value={stage}
                onChange={setStage}
              />

              <label>Timeline</label>
              <ChipGroup
                id={`${headingId}-timeline`}
                label="Timeline"
                options={TIMELINE_OPTIONS}
                value={timeline}
                onChange={setTimeline}
              />

              <button
                type="submit"
                className="btn-magenta"
                disabled={submitting}
                style={{ marginTop: 'var(--space-4)', alignSelf: 'flex-start' }}
              >
                {submitting ? 'Sending…' : 'Send brief →'}
              </button>

              {error && (
                <p className="ai-modal-error" role="alert">{error}</p>
              )}
            </form>
          </>
        ) : (
          <div
            className="ai-modal-success"
            role="status"
            aria-live="polite"
          >
            <h3 id={headingId} className="ai-modal-h3">Sent. <i>Thanks.</i></h3>
            <p>Reply incoming within 1 business day from hello@intrnauts.ai.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BriefModal;
